import React from 'react';
import PropTypes from 'prop-types';
import {Formik, isString} from 'formik';
import {Form as BsForm} from 'react-bootstrap';
import FormField from './form-field';
import FormTextField from './form-text-field';
import FormTextAreaField from './form-textarea-field';
import FormPasswordField from './form-password-field';
import FormCheckbox from './form-checkbox';
import FormNumberbox from './form-numberbox';
import FormRadioButtonGroup from './form-radio-button';
import FormSelect from './form-select';
import FormContext from '../../contexts/form-context';
import FormToggleSwitch from './form-toggle-switch';
import FormSlider from './form-slider';
import FormTypeaheadSelect from './form-typeahead-select';
import FormToggleButtonSelection from './form-toggle-button-selection';
import FormDaysOfWeek from './form-days-of-week';
import FormTimePicker from './form-time-picker';
import FormDropdownMenu from './form-dropdown-menu';
import {areArrays, areArraysEqual} from '../../utils/array-equality';
import get from '../../utils/get';

function areValuesEqual(value1, value2) {
  if (areArrays(value1, value2)) {
    return areArraysEqual(value1, value2);
  }
  return value1 === value2;
}

const Form = React.forwardRef(function FormImpl(props, ref) {
  const {children, initialValues, ...rest} = props;
  const formikRef = React.useRef();
  const [changed, setChanged] = React.useState({});

  React.useImperativeHandle(ref, () => {
    return formikRef?.current;
  });

  React.useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm();
    }
  }, []);

  React.useEffect(() => {
    setChanged({});
  }, [initialValues]);

  return (
    <Formik enableReinitialize={true} innerRef={formikRef} initialValues={initialValues} {...rest}>
      {formikBag => {
        const {handleChange, setFieldValue, ...formikBagRest} = formikBag;
        const hasChanges = Object.values(changed).some(x => !!x);

        const handleChangeWithTracking = (eventOrTextValue, maybePath) => {
          let field = maybePath;
          let value = eventOrTextValue;
          if (!isString(eventOrTextValue)) {
            field = eventOrTextValue.target.name;
            value = eventOrTextValue.target.value;
          }
          setChanged(Object.assign({}, changed, {[field]: !areValuesEqual(value, get(initialValues, field))}));
          handleChange(eventOrTextValue, maybePath);
        };

        const setFieldValueWithTracking = (field, value, shouldValidate) => {
          setChanged(Object.assign({}, changed, {[field]: !areValuesEqual(value, get(initialValues, field))}));
          setFieldValue(field, value, shouldValidate);
        };

        const newFormBag = {
          handleChange: handleChangeWithTracking,
          setFieldValue: setFieldValueWithTracking,
          changed,
          hasChanges,
          ...formikBagRest,
        };

        return (
          <FormContext.Provider
            value={{
              formikRef,
              ...newFormBag,
            }}
          >
            <BsForm noValidate onSubmit={formikBag.handleSubmit}>
              {children(newFormBag)}
            </BsForm>
          </FormContext.Provider>
        );
      }}
    </Formik>
  );
});

Form.propTypes = {
  children: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

// Form controls added here for convenience.
Form.Field = FormField;
Form.TextField = FormTextField;
Form.TextAreaField = FormTextAreaField;
Form.PasswordField = FormPasswordField;
Form.Select = FormSelect;
Form.Checkbox = FormCheckbox;
Form.Numberbox = FormNumberbox;
Form.RadioButtonGroup = FormRadioButtonGroup;
Form.ToggleSwitch = FormToggleSwitch;
Form.Slider = FormSlider;
Form.TypeaheadSelect = FormTypeaheadSelect;
Form.ToggleButtonSelection = FormToggleButtonSelection;
Form.DaysOfWeek = FormDaysOfWeek;
Form.TimePicker = FormTimePicker;
Form.DropdownMenu = FormDropdownMenu;

export default Form;
